"use client";

import { useEffect } from "react";

export function useScrollSync(
  editorRef: React.RefObject<HTMLDivElement | null>,
  previewRef: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    const editor = editorRef.current;
    const preview = previewRef.current;

    if (!editor || !preview) {
      return;
    }

    let ignoreTarget: "editor" | "preview" | null = null;
    let timeoutId: number | null = null;

    const clearIgnore = () => {
      ignoreTarget = null;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const ignore = (target: "editor" | "preview") => {
      ignoreTarget = target;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(clearIgnore, 60);
    };

    const getScrollRatio = (element: HTMLElement) => {
      const scrollHeight = element.scrollHeight - element.clientHeight;
      return scrollHeight > 0 ? element.scrollTop / scrollHeight : 0;
    };

    const applyScrollRatio = (element: HTMLElement, ratio: number) => {
      const scrollHeight = element.scrollHeight - element.clientHeight;
      if (scrollHeight > 0) {
        element.scrollTop = ratio * scrollHeight;
      }
    };

    const handleEditorScroll = () => {
      if (ignoreTarget === "editor") return;
      const ratio = getScrollRatio(editor);
      ignore("preview");
      applyScrollRatio(preview, ratio);
    };

    const handlePreviewScroll = () => {
      if (ignoreTarget === "preview") return;
      const ratio = getScrollRatio(preview);
      ignore("editor");
      applyScrollRatio(editor, ratio);
    };

    editor.addEventListener("scroll", handleEditorScroll, { passive: true });
    preview.addEventListener("scroll", handlePreviewScroll, { passive: true });

    return () => {
      editor.removeEventListener("scroll", handleEditorScroll);
      preview.removeEventListener("scroll", handlePreviewScroll);
      clearIgnore();
    };
  }, [editorRef, previewRef]);
}
